import { FC, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { Box, Modal } from "@mui/material";

import Button from "../../../components/Button";
import ContentTitle from "../../../components/ContentTitle";
import Input from "../../../components/Input";
import { CrossIcon } from "../../../assets/img/icons";
import {
    createAdminPayout,
    createPayout,
    updatePayout,
} from "../../../services/api/payout";
import { Payout } from "../../../types/Payout";
import {
    FieldErrors,
    REQUIRED_FIELD,
    clearFieldError,
    formatErrors,
    notifyFieldErrors,
    setFieldErrors,
} from "../../../utils/errors";

import "./style.css";

export type PayoutModalMode = "add" | "edit";

interface Props {
    admin: boolean;
    userId: string;
    mode: "add" | "edit";
    open: boolean;
    handleClose: () => void;
    payout?: Payout;
}

const PayoutModal: FC<Props> = ({ mode, open, handleClose, payout, admin, userId }) => {
    const dateNow = new Date();
    const currentDate = dateNow.toISOString().split("T")[0];

    const [date, setDate] = useState(currentDate);
    const [amount, setAmount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [fieldErrors, setFieldErrorsState] = useState<FieldErrors>({});

    const handleAddTrade = async () => {
        const amountErrors = setFieldErrors([
            "amount",
            !amount && REQUIRED_FIELD,
        ]);
        setFieldErrorsState(amountErrors);
        if (
            notifyFieldErrors(amountErrors, {
                message: "Le montant du payout est obligatoire",
                type: "error",
            })
        ) {
            return;
        }

        const payoutParams = {
            issueDate: new Date(`${date}T00:00:00Z`).toISOString(),
            amount: Number(amount),
        };

        try {
            setIsLoading(true);
            if (mode === "edit" && payout) {
                await updatePayout(payout.id, payoutParams);
            } else {
                admin
                    ? await createAdminPayout(userId, payoutParams)
                    : await createPayout(payoutParams);
            }

            handleClose();
            toast.success(
                `Payout ${mode === "add" ? "ajouté" : "modifié"} avec succès`
            );
        } catch (error) {
            if (error instanceof AxiosError) {
                if (error.response?.data.errors) {
                    const apiErrors = formatErrors(error.response.data.errors);
                    if (Object.keys(apiErrors).length > 0) {
                        setFieldErrorsState(apiErrors);
                        return;
                    }
                }
            }

            toast.error(
                "Une erreur s'est produite, veuillez réessayer plus tard"
            );
        } finally {
            setIsLoading(false);
        }
    };

    // Si on ferme la modal, on remet les valeurs des inputs à leur valeur initiale
    useEffect(() => {
        if (!open) {
            setDate(currentDate);
            setAmount(0);
            setFieldErrorsState({});
        }
    }, [open, currentDate]);

    // Si on reçoit une session en props, on met à jour les valeurs des inputs
    useEffect(() => {
        if (payout) {
            const date = new Date(payout.issueDate);
            setDate(date.toISOString().split("T")[0]);
            setAmount(payout.amount);
        }
    }, [payout]);

    return (
        <Modal
            keepMounted
            open={open}
            onClose={handleClose}
            aria-labelledby="keep-mounted-modal-title"
            aria-describedby="keep-mounted-modal-description"
        >
            <Box className="addTradeModal">
                <div className="addTradeModal_header">
                    <ContentTitle
                        label={`${
                            mode === "add" ? "Ajouter" : "Modifier"
                        } un payout`}
                    />
                    <CrossIcon
                        className="addTradeModal_header_close"
                        onClick={handleClose}
                    />
                </div>
                <div className="addTradeModal_input_line">
                    <p>Date</p>
                    <Input
                        value={date}
                        onChanged={(value) => {
                            setDate(value);
                            setFieldErrorsState((prev) =>
                                clearFieldError(prev, "issueDate")
                            );
                        }}
                        className="addTradeModal_input"
                        type="date"
                        max={new Date().toISOString().split("T")[0]}
                        error={fieldErrors.issueDate || fieldErrors.date}
                    />
                </div>
                <div className="addTradeModal_input_line">
                    <p>Montant (en $)</p>
                    <Input
                        value={amount}
                        onChanged={(value) => {
                            setAmount(value);
                            setFieldErrorsState((prev) =>
                                clearFieldError(prev, "amount")
                            );
                        }}
                        className="addTradeModal_input"
                        type="number"
                        error={fieldErrors.amount}
                    />
                </div>
                <Button
                    className="addTradeModal_button"
                    size="medium"
                    label={`${mode === "add" ? "Ajouter" : "Modifier"}`}
                    onClick={handleAddTrade}
                    isLoading={isLoading}
                />
            </Box>
        </Modal>
    );
};

export default PayoutModal;
